// Full-page subscribe flow (/subscribe/plan then /subscribe/payment), replacing
// the plan selection modal and the old checkout while the switch is on.
import personalIcon from '@/assets/icons/circle-personal.svg'
import commercialIcon from '@/assets/icons/circle-commercial.svg'
import extendedIcon from '@/assets/icons/circle-extended.svg'

export const SUBSCRIBE_FLOW_SWITCH = 'clean_subscribe_flow'

export const TRIAL_DAYS = 30

const PLAN_ICONS = { personal: personalIcon, commercial: commercialIcon, extended: extendedIcon }

export function planIcon (slug) {
  return PLAN_ICONS[slug] || null
}

const CURRENCY_SYMBOLS = { gbp: '£', eur: '€', usd: '$' }

// Prices come from the plans API in the visitor's currency, in minor units.
// Whole amounts drop the decimals: "£22", but "£18.33".
export function formatPrice (amount, currency) {
  const code = (currency || '').toLowerCase()
  const symbol = CURRENCY_SYMBOLS[code]
  const decimals = amount % 100 === 0 ? 0 : 2
  const value = (amount / 100).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  return symbol ? `${symbol}${value}` : `${value} ${code.toUpperCase()}`.trim()
}

// The API lists each plan once per billing interval; this pairs them up by
// slug, cheapest first, as { slug, name, description, features, month, year }
// where month and year are the plans themselves (either may be missing).
export function groupPlans (plans) {
  const bySlug = {}
  plans.forEach((plan) => {
    const group = bySlug[plan.slug] || (bySlug[plan.slug] = {
      slug: plan.slug,
      name: plan.product_name,
      description: plan.description,
      features: planFeatures(plan),
      month: null,
      year: null
    })
    group[plan.interval] = plan
  })
  const sortAmount = (group) => (group.month || group.year).amount * (group.month ? 12 : 1)
  return Object.values(bySlug).sort((a, b) => sortAmount(a) - sortAmount(b))
}

export function planFeatures (plan) {
  return (plan.details || '').split('\n').map((line) => line.trim()).filter(Boolean)
}

// What a plan works out at per month, for comparing intervals.
export function monthlyAmount (plan) {
  return plan.interval === 'year' ? Math.round(plan.amount / 12) : plan.amount
}

// How much paying yearly saves over twelve monthly payments, or 0 if the
// group doesn't have both.
export function yearlySaving (group) {
  if (!group.month || !group.year) {
    return 0
  }
  return Math.max(0, group.month.amount * 12 - group.year.amount)
}

// Whole months free when paying yearly ("2 months free"), rounded down.
export function monthsFreeYearly (group) {
  if (!group.month || !yearlySaving(group)) {
    return 0
  }
  return Math.floor(yearlySaving(group) / group.month.amount)
}

// Where a logged-in user goes when something needs a subscription. With no
// current subscription that's the new plan page; one that is current but not
// usable (past due) is sorted out on the subscription page.
export function subscriptionNeededRoute (team) {
  if (team && team.current_subscription) {
    return { name: 'subscription' }
  }
  return { name: 'subscribe-plan' }
}

// Sends a logged-in user who needs a subscription on to get one, remembering
// the page they were on so the confirmation can take them back there.
export function goToSubscribe (router, team) {
  const location = subscriptionNeededRoute(team)
  const current = router.currentRoute
  if (location.name === 'subscribe-plan' && current && current.fullPath !== '/' && !current.name?.startsWith('subscribe-')) {
    location.query = { next: current.fullPath }
  }
  // Already on the page: pushing again would only raise NavigationDuplicated.
  return router.push(location).catch(() => {})
}

function applyCoupon (amount, coupon) {
  if (coupon.percent_off) {
    return Math.round(amount * (100 - coupon.percent_off) / 100)
  }
  if (coupon.amount_off) {
    return Math.max(0, amount - coupon.amount_off)
  }
  return amount
}

// What the next payment will be, and what later ones go back to if a discount
// code only covers the next. Stripe applies a code from the first paid invoice,
// so a "once" code isn't used up by a free trial: it discounts the charge when
// the trial ends. Without a trial today's payment uses it, so the renewal is
// discounted only by a "forever" code, or a "repeating" one that lasts past
// the first billing period. A repeating code always runs out, so later is the
// full price for those even if a few more payments are still discounted.
// Returns { next, later } in minor units; later is null when it equals next.
export function upcomingCharges (plan, coupon, { trial = false } = {}) {
  const full = plan.amount
  if (!coupon) {
    return { next: full, later: null }
  }
  const periodMonths = plan.interval === 'year' ? 12 : 1
  // Months a repeating code has already run by the next payment.
  const monthsUsed = trial ? TRIAL_DAYS / 30 : periodMonths
  let coversNext
  let coversLater
  if (coupon.duration === 'forever') {
    coversNext = true
    coversLater = true
  } else if (coupon.duration === 'once') {
    coversNext = trial
    coversLater = false
  } else {
    const months = coupon.duration_in_months || 0
    coversNext = months > monthsUsed
    coversLater = false
  }
  const next = coversNext ? applyCoupon(full, coupon) : full
  const later = coversLater ? applyCoupon(full, coupon) : full
  return { next, later: later === next ? null : later }
}
